<template>
    <div v-if="locations.length" class="map-wrapper" :style="{ paddingBottom: height + '%' }">
        <div
            ref="map"
            class="product-location-map"
            role="region"
            :aria-label="hotel ? 'Hotel and destination map' : 'Parking lot and destination map'"
        ></div>
    </div>
</template>

<script>
import { map, tileLayer, marker, icon } from 'leaflet';

export default {
    props: {
        parkingLot: {
            type: Object,
            default: null
        },
        hotel: {
            type: Object,
            default: null
        },
        airport: {
            type: Object,
            default: null
        },
        isMobileMap: {
            type: Boolean,
            default: false
        },
        height: {
            type: Number,
            default: 47
        }
    },

    computed: {
        locations() {
            const isCruisePort = this.airport && this.airport.type === 'CruisePort';
            const property = this.hotel || this.parkingLot;
            const propertyCoordinates = this.hotel ? this.hotel.address : property && property.geo;
            const destinationCoordinates = this.hotel ? this.airport : this.airport && this.airport.geo;
            return [
                {
                    coordinates: this.getCoordinates(propertyCoordinates),
                    image: this.hotel ? 'hotel-product-pin-new-sm.png' : 'lot-product-pin-new.png',
                    label: (property && property.name) || (this.hotel ? 'Hotel' : 'Parking lot')
                },
                {
                    coordinates: this.getCoordinates(destinationCoordinates),
                    image: isCruisePort ? 'cruise-pin-new-grey.png' : 'airport-pin-new-black-sm.png',
                    // The cruise pin image is 100x76, twice the size of the other pins.
                    scale: isCruisePort ? 0.5 : 1,
                    label: (this.airport && (this.airport.fullname || this.airport.name)) || (isCruisePort ? 'Cruise port' : 'Airport')
                }
            ].filter(location => location.coordinates);
        }
    },

    mounted() {
        if (!this.locations.length) {
            return;
        }

        this._markerImages = [];
        this._map = map(this.$refs.map, {
            zoomSnap: 0.1,
            dragging: true,
            scrollWheelZoom: false,
            touchZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            keyboard: false
        });

        tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(this._map);

        this.centerMap(40, 40);

        Promise.all(this.locations.map(location => this.addMarker(location))).then(sizes => {
            if (!this._map) {
                return;
            }

            const width = Math.max(40, ...sizes.map(size => size ? size.width / 2 + 10 : 0));
            const height = Math.max(40, ...sizes.map(size => size ? size.height + 10 : 0));
            this.centerMap(width, height);
        });

        this.$nextTick(() => {
            if (this._map) {
                this._map.invalidateSize();
            }
        });
    },

    beforeDestroy() {
        (this._markerImages || []).forEach(image => {
            image.onload = null;
            image.onerror = null;
        });
        if (this._map) {
            this._map.remove();
            this._map = null;
        }
    },

    methods: {
        centerMap(horizontalPadding, verticalPadding) {
            // Project all valid marker coordinates at zoom 0 to calculate screen-space distances.
            const points = this.locations.map(location => this._map.project(location.coordinates, 0));
            // Use midpoint centering only for mobile hotel maps.
            const useMidpoint = this.isMobileMap && Boolean(this.hotel);
            // Center lot and desktop maps on the property; center mobile hotel maps between the markers.
            const centerPoint = useMidpoint
                ? points[0].add(points[points.length - 1]).divideBy(2)
                : points[0];
            // Use the property's coordinates or convert the projected midpoint back to latitude and longitude.
            const center = useMidpoint
                ? this._map.unproject(centerPoint, 0)
                : this.locations[0].coordinates;
            // Calculate each marker's horizontal and vertical offset from the center at the same zoom.
            const offsets = points.map(point => point.subtract(centerPoint));
            // Find the largest horizontal distance, regardless of direction.
            const horizontalDistance = Math.max(...offsets.map(offset => Math.abs(offset.x)));
            // Find the largest vertical distance, regardless of direction.
            const verticalDistance = Math.max(...offsets.map(offset => Math.abs(offset.y)));
            // Read the map container's current width and height in pixels.
            const size = this._map.getSize();
            // Reserve horizontal space for marker images on both sides of the map.
            const availableWidth = Math.max(1, size.x - horizontalPadding * 2);
            // Reserve vertical space for marker images at the top and bottom of the map.
            const availableHeight = Math.max(1, size.y - verticalPadding * 2);
            // Calculate the horizontal scale using equal space on either side of the fixed center.
            const horizontalScale = horizontalDistance ? availableWidth / (horizontalDistance * 2) : Infinity;
            // Calculate the vertical scale; a zero distance does not limit the zoom.
            const verticalScale = verticalDistance ? availableHeight / (verticalDistance * 2) : Infinity;
            // Use the smaller scale so markers fit within both dimensions.
            const scale = Math.min(horizontalScale, verticalScale);
            // Convert the scale to a Leaflet zoom level; use zoom 15 when all markers share the same coordinates.
            const zoom = Number.isFinite(scale) ? this._map.getScaleZoom(scale, 0) : 15;

            // Round down to a tenth of a zoom level to fit markers without unnecessarily zooming out.
            const fittedZoom = Math.floor(zoom * 10) / 10;
            // Limit zoom to 0-15 and apply the selected center without animation.
            this._map.setView(center, Math.max(0, Math.min(15, fittedZoom)), { animate: false });
        },

        getCoordinates(geo) {
            if (!geo || [geo.latitude, geo.longitude].some(value =>
                !['number', 'string'].includes(typeof value) || String(value).trim() === ''
            )) {
                return null;
            }

            const latitude = Number(geo.latitude);
            const longitude = Number(geo.longitude);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude) ||
                Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
                return null;
            }

            return [latitude, longitude];
        },

        addMarker(location) {
            return new Promise(resolve => {
                const image = new Image();
                this._markerImages.push(image);
                image.onload = () => {
                    if (!this._map) {
                        resolve(null);
                        return;
                    }

                    const markerScale = (this.isMobileMap ? 0.85 : 1) * (location.scale || 1);
                    const width = image.naturalWidth * markerScale;
                    const height = image.naturalHeight * markerScale;
                    marker(location.coordinates, {
                        icon: icon({
                            iconUrl: image.src,
                            iconSize: [width, height],
                            iconAnchor: [width / 2, height]
                        }),
                        title: location.label,
                        alt: location.label
                    }).addTo(this._map);
                    resolve({ width, height });
                };
                image.onerror = () => resolve(null);
                image.src = apr.storage_url + '/img/icons/' + location.image;
            });
        }
    }
};
</script>

<style scoped>
.product-location-map {
    position: absolute; 
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 0;
    width: 100%;
}
.map-wrapper {
    position: relative;
    width: 100%;
    padding-bottom: 47%;
}
</style>